import React, { useEffect, useState } from 'react';
import { LogOut, Bell, User, Menu } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { BrandLockup } from '@/components/brand/BrandMark';
import { messagesApi } from '@/lib/api';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { NAV, AUTH } from '@/constants/testIds';
import { useAuth } from '@/lib/auth';
import { useNavigate } from 'react-router-dom';

export function Topbar({ onOpenNav }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);
  const initials = (user?.name || 'U').split(' ').map(s => s[0]).slice(0, 2).join('').toUpperCase();

  // Staff-only: customers have no inbox threads endpoint.
  useEffect(() => {
    if (user?.role === 'customer') return;
    let cancelled = false;
    messagesApi.threads()
      .then(rows => {
        if (cancelled) return;
        setUnread(rows.reduce((sum, t) => sum + (t.unread_count || 0), 0));
      })
      .catch(() => { /* badge is non-critical; the interceptor reports real failures */ });
    return () => { cancelled = true; };
  }, [user?.role]);

  return (
    <header data-testid={NAV.topbar} className="sticky top-0 z-40 h-14 border-b bg-card/85 backdrop-blur shadow-xs supports-[backdrop-filter]:bg-card/70">
      <div className="flex h-full items-center justify-between px-4 md:px-6 lg:px-8">
        <div className="flex items-center gap-1">
          {onOpenNav && (
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden -ml-2"
              onClick={onOpenNav}
              aria-label="Open navigation"
              data-testid="topbar-menu-button"
            >
              <Menu className="h-5 w-5" />
            </Button>
          )}
          <BrandLockup className="lg:hidden" markClassName="h-7 w-7" />
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            className="relative"
            onClick={() => navigate('/inbox')}
            aria-label={unread > 0 ? `Notifications, ${unread} unread` : 'Notifications'}
          >
            <Bell className="h-4 w-4" />
            {unread > 0 && (
              <span className="absolute right-1 top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-semibold leading-none text-destructive-foreground">
                {unread > 9 ? '9+' : unread}
              </span>
            )}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="topbar-user-menu-button">
                <Avatar className="h-8 w-8">
                  <AvatarFallback>{initials}</AvatarFallback>
                </Avatar>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div className="text-sm font-medium">{user?.name}</div>
                <div className="text-xs text-muted-foreground">{user?.email}</div>
                <div className="mt-1 inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium">{user?.role}</div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate('/settings')}>
                <User className="mr-2 h-4 w-4" /> Account & Settings
              </DropdownMenuItem>
              <DropdownMenuItem onClick={logout} data-testid={AUTH.logoutButton}>
                <LogOut className="mr-2 h-4 w-4" /> Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}
